import { useEffect, useMemo, useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Check,
  CheckCircle2,
  Archive,
  BellOff,
  Filter,
  Eye,
  MessageSquare,
  Bell,
  Search,
  Mail,
  Plus,
  ChevronDown,
  Inbox,
  FileEdit,
  Star,
  Send,
  type LucideIcon,
} from "lucide-react";
import { LoadingSpinner } from "@/assets/icons/LoadingSpinner";
import { cn } from "@/utils/Functions";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Combobox,
  ComboboxTrigger,
  ComboboxPopup,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
} from "@/components/ui/combobox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectPopup,
  SelectItem,
} from "@/components/ui/Select";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsiblePanel,
} from "@/components/ui/collapsible";
import {
  NotificationItem,
  NotificationDetail,
  MessageComponent,
  ThreadLabel,
} from "./components";
import { useInboxData } from "@/hooks/inbox/useInboxData";
import { filterOptions, defaultTypeOrder } from "@/constants/inbox.constants";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import {
  useNylasConnections,
  useNylasThreads,
  useNylasMessages,
  useConnectEmail,
} from "@/hooks";
import type { NylasMessage } from "@equaliq/api-model-v2";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";

type ViewMode = "notifications" | "conversations";
type FilterTab = "all" | "unread" | "actionRequired";
type InboxFolder = "inbox" | "drafts" | "important" | "sent";

const INBOX_FOLDER_ITEMS: { id: InboxFolder; label: string; icon: LucideIcon }[] = [
  { id: "inbox", label: "Inbox", icon: Inbox },
  { id: "drafts", label: "Drafts", icon: FileEdit },
  { id: "important", label: "Important", icon: Star },
  // { id: "sent", label: "Sent", icon: Send },
];

function InboxFolderRail({ children }: { children: ReactNode }) {
  const { open } = useSidebar();
  return (
    <div
      className={cn(
        "flex h-full shrink-0 overflow-hidden border-r border-sidebar-border transition-[width] duration-200 ease-out",
        open ? "w-(--sidebar-width)" : "w-0"
      )}
    >
      {children}
    </div>
  );
}

function InboxPage() {
  const isDemo = useIsDemo();
  const { initialNotifications, initialThreads, initialMessages } = useInboxData();

  const {
    connections,
    isLoading: connectionsLoading,
    refetch: refetchConnections,
  } = useNylasConnections({ enabled: !isDemo });
  const { connectEmail, isConnecting } = useConnectEmail();

  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const {
    threads,
    isLoading: threadsLoading,
  } = useNylasThreads(!isDemo ? selectedConnectionId : null);

  const [viewMode, setViewMode] = useState<ViewMode>("conversations");
  const [notifications, setNotifications] = useState(initialNotifications);
  const [selectedIds, setSelectedIds] = useState(() => new Set<string>());
  const [filterTab, setFilterTab] = useState<FilterTab>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [mailFolder, setMailFolder] = useState<InboxFolder>("inbox");

  useEffect(() => {
    setNotifications(initialNotifications);
  }, [initialNotifications]);

  useEffect(() => {
    if (!isDemo && !selectedConnectionId && connections.length > 0) {
      setSelectedConnectionId(connections[0].connectionId);
    }
  }, [isDemo, connections, selectedConnectionId]);

  const isThreadsView = viewMode === "conversations";
  const threadSource = isDemo ? initialThreads : threads;

  const threadLabelOptions = useMemo(() => {
    if (!isThreadsView) return [];
    const unique = new Set(threadSource.flatMap((t) => t.labels ?? []).map((l) => l.toLowerCase()));
    return Array.from(unique).sort().map((l) => ({
      value: l,
      label: l.charAt(0).toUpperCase() + l.slice(1),
    }));
  }, [isThreadsView, threadSource]);

  const activeFilterOptions = isThreadsView
    ? [{ value: "all", label: "All Threads" }, ...threadLabelOptions]
    : filterOptions;

  // Derive effective filter — reset to "all" when current value isn't in options
  const effectiveTypeFilter =
    typeFilter !== "all" && !activeFilterOptions.some((o) => o.value === typeFilter)
      ? "all"
      : typeFilter;

  const filteredThreads = useMemo(() => threadSource.filter((t) => {
    if (mailFolder !== "inbox" && !(t.labels?.some((l) => l.toLowerCase() === mailFolder) ?? false)) {
      return false;
    }
    if (effectiveTypeFilter === "all") return true;
    return t.labels?.some((l) => l.toLowerCase() === effectiveTypeFilter) ?? false;
  }), [threadSource, mailFolder, effectiveTypeFilter]);

  const filteredNotifications = useMemo(() => {
    const filtered = notifications.filter((n) => {
      if (mailFolder === "important" && !n.actionRequired) return false;
      if (mailFolder === "drafts" || mailFolder === "sent") return false;
      if (n.status === "archived") return false;
      if (filterTab === "unread" && n.status !== "unread") return false;
      if (filterTab === "actionRequired" && !n.actionRequired) return false;
      if (effectiveTypeFilter !== "all" && n.type !== effectiveTypeFilter) return false;
      return true;
    });
    return [...filtered].sort((a, b) => defaultTypeOrder.indexOf(a.type) - defaultTypeOrder.indexOf(b.type));
  }, [notifications, mailFolder, filterTab, effectiveTypeFilter]);

  const handleAddConnection = async () => {
    const success = await connectEmail();
    if (success) {
      await refetchConnections();
    }
  };

  const handleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredNotifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredNotifications.map((item) => item.id)));
    }
  };

  const handleMarkRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, status: n.status === "unread" ? "read" : "unread" } : n
      )
    );
  };

  const handleMarkAllRead = () => {
    setNotifications((prev) =>
      prev.map((n) => (selectedIds.has(n.id) ? { ...n, status: "read" } : n))
    );
    setSelectedIds(new Set());
  };

  const handleArchive = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, status: "archived" } : n))
    );
  };

  const selectedNotification = notifications.find((n) => n.id === selectedItemId);
  const selectedThread = threadSource.find((t) => t.threadMetadataId === selectedThreadId) ?? null;

  const handleViewDetail = (id: string) => {
    setSelectedItemId(id);
    const item = notifications.find((n) => n.id === id);
    if (item?.status === "unread") {
      handleMarkRead(id);
    }
  };

  const handleCloseDetail = () => {
    setSelectedItemId(null);
  };

  const resetSelections = () => {
    setSelectedItemId(null);
    setSelectedIds(new Set());
    setSelectedThreadId(null);
  };

  const handleMailFolder = (folder: InboxFolder) => {
    setMailFolder(folder);
    setSelectedItemId(null);
    setSelectedThreadId(null);
    setSelectedIds(new Set());
  };


  return (
    <SidebarProvider
      defaultOpen
      disableKeyboardShortcut
      persistState={false}
      className="flex h-full min-h-0 w-full flex-1 flex-row overflow-hidden"
    >
      <InboxFolderRail>
        <Sidebar collapsible="none" className="border-0">
          <SidebarHeader className="border-b border-sidebar-border">
            <SidebarGroupLabel className="px-2">Navigation</SidebarGroupLabel>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarMenu>
                {INBOX_FOLDER_ITEMS.map((item) => (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      isActive={mailFolder === item.id}
                      onClick={() => handleMailFolder(item.id)}
                      tooltip={item.label}
                    >
                      <item.icon />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>
      </InboxFolderRail>

      <SidebarInset className="min-h-0 flex flex-1 flex-col overflow-hidden">
      <header className="py-2 px-4 bg-background z-10 border-b border-border/50 shrink-0">
        <div className="flex items-center gap-3">
          <SidebarTrigger className="mt-0! ml-0! shrink-0" />
          <h1 className="text-base font-semibold shrink-0">Inbox</h1>

          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search messages..."
              className="w-full bg-muted/50 text-sm text-foreground placeholder:text-muted-foreground/50 rounded-lg pl-9 pr-3 py-1.5 outline-none focus:ring-1 focus:ring-primary/30 transition-shadow"
            />
          </div>

          <div className="flex items-center gap-1 ml-auto">
            <Tabs
              value={viewMode}
              onValueChange={(v) => {
                setViewMode(v as ViewMode);
                resetSelections();
              }}
            >
              <TabsList variant="pill" indicatorColor="secondary">
                <TabsTrigger value="conversations">
                  <MessageSquare className="size-3.5" />
                  {isDemo ? "Conversations" : "Threads"}
                </TabsTrigger>
                <TabsTrigger value="notifications">
                  <Bell className="size-3.5" />
                  {isDemo ? "Notifications" : "Updates"}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Select
              value={effectiveTypeFilter}
              onValueChange={(val) => setTypeFilter(val ?? "all")}
              items={activeFilterOptions}
            >
              <SelectTrigger size="sm" className="w-auto min-w-0 border-none bg-transparent shadow-none text-muted-foreground">
                <Filter className="size-3.5 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectPopup>
                {activeFilterOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>

            {!isDemo && viewMode === "conversations" && connections.length > 0 && (
              <Combobox
                value={selectedConnectionId || ""}
                onValueChange={(val) => {
                  if (val === "add-new") {
                    handleAddConnection();
                  } else {
                    setSelectedConnectionId(val);
                  }
                }}
              >
                <ComboboxTrigger
                  render={<Button variant="ghost" size="sm" className="text-muted-foreground" />}
                >
                  <Mail className="size-3.5 mr-1" />
                  {connections.find((c) => c.connectionId === selectedConnectionId)?.email || "Select account"}
                </ComboboxTrigger>
                <ComboboxPopup className={cn("w-64")}>
                  <ComboboxList>
                    <ComboboxEmpty>No accounts</ComboboxEmpty>
                    {connections.map((conn) => (
                      <ComboboxItem key={conn.connectionId} value={conn.connectionId}>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{conn.email}</span>
                          <span className="text-xs text-muted-foreground">{conn.provider}</span>
                        </div>
                      </ComboboxItem>
                    ))}
                    <div className="border-t border-border my-1" />
                    <ComboboxItem value="add-new">
                      <Plus className="size-3.5 mr-2" />
                      Add another account
                    </ComboboxItem>
                  </ComboboxList>
                </ComboboxPopup>
              </Combobox>
            )}

            {viewMode === "notifications" && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => {
                  setNotifications((prev) => prev.map((n) => ({ ...n, status: "read" as const })));
                }}
              >
                <Check className="size-4 mr-1.5" />
                Mark all read
              </Button>
            )}

          </div>
        </div>
      </header>

      {/* Sub-toolbar for notifications */}
      {viewMode === "notifications" && (
        <>
          <div className="px-4 py-2 border-b border-border/50 flex items-center justify-between gap-4">
            <Tabs value={filterTab} onValueChange={(v) => setFilterTab(v as FilterTab)}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="unread">Unread</TabsTrigger>
                <TabsTrigger value="actionRequired">Action Required</TabsTrigger>
              </TabsList>
            </Tabs>

            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
                <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
                  <CheckCircle2 className="size-4 mr-1" />
                  Mark read
                </Button>
                <Button variant="ghost" size="sm">
                  <Archive className="size-4 mr-1" />
                  Archive
                </Button>
              </div>
            )}
          </div>

          {filteredNotifications.length > 0 && (
            <div className="px-4 py-2 border-b border-border/30 flex items-center gap-3">
              <Checkbox
                checked={selectedIds.size === filteredNotifications.length}
                indeterminate={selectedIds.size > 0 && selectedIds.size < filteredNotifications.length}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-xs text-muted-foreground">
                {selectedIds.size === filteredNotifications.length ? "Deselect all" : "Select all"}
              </span>
            </div>
          )}
        </>
      )}

      <div className="flex-1 flex overflow-hidden">
        {viewMode === "conversations" && (
          <ThreadView
            connections={isDemo ? [{ connectionId: "demo-conn", email: "demo@equaliq.com" }] : connections}
            connectionsLoading={isDemo ? false : connectionsLoading}
            threads={filteredThreads}
            threadsLoading={isDemo ? false : threadsLoading}
            selectedConnectionId={isDemo ? "demo-conn" : selectedConnectionId}
            selectedThreadId={selectedThreadId}
            selectedThread={selectedThread}
            onSelectThread={setSelectedThreadId}
            onAddConnection={isDemo ? () => {} : handleAddConnection}
            isConnecting={isDemo ? false : isConnecting}
            isDemo={isDemo}
            demoMessages={isDemo ? initialMessages : undefined}
          />
        )}


        {viewMode === "notifications" && (
          <>
            <div
              className={cn(
                "overflow-y-auto border-r border-border/50 p-2",
                selectedItemId ? "hidden lg:block lg:w-[40%]" : "flex-1"
              )}
            >
              {filteredNotifications.length === 0 ? (
                <EmptyListState filterTab={filterTab} label="notifications" />
              ) : (
                filteredNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    selected={selectedIds.has(notification.id)}
                    isDetailViewActive={selectedItemId === notification.id}
                    onSelect={handleSelect}
                    onViewDetail={handleViewDetail}
                    onMarkRead={handleMarkRead}
                    onArchive={handleArchive}
                  />
                ))
              )}
            </div>

            <AnimatePresence mode="wait">
              {selectedItemId && selectedNotification ? (
                <motion.div
                  key={selectedItemId}
                  initial={{ opacity: 0, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 0 }}
                  transition={{ duration: 0.1 }}
                  className="flex-1 flex flex-col overflow-y-auto lg:w-[60%]"
                >
                  <NotificationDetail
                    notification={selectedNotification}
                    onClose={handleCloseDetail}
                    onMarkRead={handleMarkRead}
                    onArchive={handleArchive}
                  />
                </motion.div>
              ) : (
                <EmptyDetailPane label="a notification" />
              )}
            </AnimatePresence>
          </>
        )}
      </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

function EmptyListState({
  filterTab,
  label,
}: {
  filterTab: string;
  label: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center h-full py-16 text-center">
      <div className="p-4 rounded-full bg-muted/50 mb-4">
        <BellOff className="size-8 text-muted-foreground" />
      </div>
      <h3 className="font-medium text-foreground mb-1">
        {filterTab === "unread"
          ? "All caught up!"
          : filterTab === "actionRequired"
            ? "No actions required"
            : `No ${label}`}
      </h3>
      <p className="text-sm text-muted-foreground max-w-xs">
        {filterTab === "unread"
          ? `You've read all your ${label}.`
          : filterTab === "actionRequired"
            ? "You have no pending actions at the moment."
            : `When you receive ${label}, they'll appear here.`}
      </p>
    </div>
  );
}

function EmptyDetailPane({ label }: { label: string }) {
  return (
    <motion.div
      key="empty"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="hidden lg:flex lg:w-[60%] items-center justify-center border-l border-border/50 bg-muted/5"
    >
      <div className="text-center max-w-sm px-6">
        <div className="p-4 rounded-full bg-muted/50 mb-4 inline-flex">
          <Eye className="size-6 text-muted-foreground" />
        </div>
        <h3 className="font-medium text-foreground mb-2">Select {label}</h3>
        <p className="text-sm text-muted-foreground">
          Choose {label} from the list to view its details here.
        </p>
      </div>
    </motion.div>
  );
}

function ThreadView({
  connections,
  connectionsLoading,
  threads,
  threadsLoading,
  selectedConnectionId,
  selectedThreadId,
  selectedThread,
  onSelectThread,
  onAddConnection,
  isConnecting,
  isDemo,
  demoMessages,
}: {
  connections: { connectionId: string; email: string }[];
  connectionsLoading: boolean;
  threads: { threadMetadataId: string; externalThreadId: string; summary?: string | null; priority?: number | null; labels?: string[] | null; ragKeywords?: string[] | null; messageCount: number; lastMessageAt: string; analyzed: boolean; analyzedAt?: string | null }[];
  threadsLoading: boolean;
  selectedConnectionId: string | null;
  selectedThreadId: string | null;
  selectedThread: { threadMetadataId: string; externalThreadId: string; summary?: string | null; priority?: number | null; labels?: string[] | null; ragKeywords?: string[] | null; messageCount: number; lastMessageAt: string; analyzed: boolean; analyzedAt?: string | null } | null;
  onSelectThread: (id: string) => void;
  onAddConnection: () => void;
  isConnecting: boolean;
  isDemo: boolean;
  demoMessages?: NylasMessage[];
}) {
  const {
    messages: fetchedMessages,
    isLoading: messagesLoading,
  } = useNylasMessages(!isDemo ? selectedConnectionId : null);

  const allMessages = isDemo && demoMessages ? demoMessages : fetchedMessages;

  const threadMessages: NylasMessage[] = selectedThread
    ? allMessages.filter((m) => m.threadId === selectedThread.externalThreadId)
    : [];

  if (connectionsLoading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <LoadingSpinner className="size-8 animate-spin text-muted-foreground [animation-duration:0.5s]" />
      </div>
    );
  }

  if (connections.length === 0) {
    return <ConnectEmailPrompt onAdd={onAddConnection} isConnecting={isConnecting} label="threads" />;
  }

  if (!selectedConnectionId) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <p className="text-muted-foreground">Select an email account</p>
      </div>
    );
  }

  if (threadsLoading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <LoadingSpinner className="size-8 animate-spin text-muted-foreground [animation-duration:0.5s]" />
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="text-center">
          <MessageSquare className="size-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-lg font-medium">No threads</p>
          <p className="text-sm text-muted-foreground">Threads will appear here once emails are analyzed</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          "overflow-y-auto border-r border-border/50 p-2 pr-1 bg-neutral-50 dark:bg-black/20",
          selectedThreadId ? "hidden lg:block lg:w-[40%]" : "flex-1"
        )}
      >
        <div className="flex flex-col gap-0 bg-background border border-border/50 rounded-xl overflow-hidden">
          {threads.map((thread) => (
            <div
              key={thread.threadMetadataId}
              onClick={() => onSelectThread(thread.threadMetadataId)}
              className={cn(
                "p-3 cursor-pointer transition-colors not-last:border-b border-border/50",
                selectedThreadId === thread.threadMetadataId ? "bg-accent" : "hover:bg-accent/50",
                !thread.analyzed && "bg-accent/20"
              )}
            >
            <div className="flex items-start gap-2">
              {thread.priority && thread.priority >= 3 && (
                <div
                  className={cn(
                    "size-2 rounded-full mt-1.5 shrink-0",
                    thread.priority >= 4 ? "bg-red-500" : "bg-yellow-500"
                  )}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="text-sm font-medium truncate">
                    {thread.summary || `Thread ${thread.externalThreadId.substring(0, 12)}...`}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {new Date(thread.lastMessageAt).toLocaleDateString()}
                  </span>
                </div>
                {thread.labels && thread.labels.length > 0 && (
                  <div className="flex gap-1 mb-1 flex-wrap">
                    {thread.labels.slice(0, 3).map((label, i) => (
                      <ThreadLabel key={i} label={label} size="sm" />
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {thread.messageCount} {thread.messageCount === 1 ? "message" : "messages"}
                  {thread.priority && ` \u2022 Priority ${thread.priority}`}
                  {!thread.analyzed && " \u2022 Pending analysis"}
                </p>
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {selectedThread ? (
          <motion.div
            key={selectedThreadId}
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 0 }}
            transition={{ duration: 0.1 }}
            className="flex-1 flex flex-col overflow-y-auto lg:w-[60%] p-6"
          >
            <h2 className="text-2xl font-semibold mb-4">
              {selectedThread.summary || "Thread Details"}
            </h2>
            <div className="space-y-3 text-sm">
              {selectedThread.priority && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground min-w-24">Priority:</span>
                  <span
                    className={cn(
                      "font-medium",
                      selectedThread.priority >= 4 ? "text-red-500" : selectedThread.priority >= 3 ? "text-yellow-500" : ""
                    )}
                  >
                    {selectedThread.priority} / 5
                  </span>
                </div>
              )}
              {selectedThread.labels && selectedThread.labels.length > 0 && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground min-w-24">Labels:</span>
                  <div className="flex gap-2 flex-wrap">
                    {selectedThread.labels.map((label, i) => (
                      <ThreadLabel key={i} label={label} size="md" />
                    ))}
                  </div>
                </div>
              )}
              {selectedThread.ragKeywords && selectedThread.ragKeywords.length > 0 && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground min-w-24">Keywords:</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {selectedThread.ragKeywords.map((keyword, i) => (
                      <span key={i} className="px-2 py-0.5 rounded border border-border text-xs">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <span className="text-muted-foreground min-w-24">Messages:</span>
                <span>{selectedThread.messageCount}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-muted-foreground min-w-24">Last message:</span>
                <span>{new Date(selectedThread.lastMessageAt).toLocaleString()}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-muted-foreground min-w-24">Status:</span>
                <span>
                  {selectedThread.analyzed ? (
                    <>
                      Analyzed{" "}
                      {selectedThread.analyzedAt &&
                        `on ${new Date(selectedThread.analyzedAt).toLocaleDateString()}`}
                    </>
                  ) : (
                    <span className="text-yellow-500">Pending analysis</span>
                  )}
                </span>
              </div>
            </div>

            <div className="mt-6 border-t border-border/50 pt-4">
              <Collapsible defaultOpen={true}>
                <CollapsibleTrigger className="flex items-center gap-2 w-full text-left group">
                  <ChevronDown className="size-4 text-muted-foreground transition-transform group-data-panel-open:rotate-180" />
                  <Mail className="size-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    Messages ({messagesLoading ? "..." : threadMessages.length})
                  </span>
                </CollapsibleTrigger>
                <CollapsiblePanel>
                  <div className="mt-3 space-y-2">
                    {messagesLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <LoadingSpinner className="size-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : threadMessages.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-2">
                        No messages found for this thread.
                      </p>
                    ) : (
                      [...threadMessages]
                        .sort((a, b) => a.date - b.date)
                        .map((message) => (
                          <MessageComponent
                            key={message.id}
                            message={message}
                          />
                        ))
                    )}
                  </div>
                </CollapsiblePanel>
              </Collapsible>
            </div>
          </motion.div>
        ) : (
          <EmptyDetailPane label="a thread" />
        )}
      </AnimatePresence>
    </>
  );
}

function ConnectEmailPrompt({
  onAdd,
  isConnecting,
  label = "messages",
}: {
  onAdd: () => void;
  isConnecting: boolean;
  label?: string;
}) {
  return (
    <div className="flex items-center justify-center h-full w-full">
      <div className="text-center max-w-md">
        <Mail className="size-16 mx-auto mb-6 text-muted-foreground" />
        <h2 className="text-2xl font-semibold mb-2">Connect your email</h2>
        <p className="text-muted-foreground mb-6">
          Connect your email account to start managing your {label} in EqualIQ.
        </p>
        <Button onClick={onAdd} disabled={isConnecting}>
          {isConnecting ? (
            <>
              <LoadingSpinner className="size-4 mr-2 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <Plus className="size-4 mr-2" />
              Connect Email Account
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default InboxPage;
